import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useCallback, useEffect, useMemo, useState } from "react";
import { NumericFormat } from "react-number-format";

import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import DeleteIconButton from "_shared/_views/buttons/delete-icon-button";
import LoadingButton from "_shared/_views/loading/loading-button";
import Guid from "_shared/guids/guid";
import { percentageValue } from "_shared/percentages/percent";
import Document from "documents/document";
import DocumentSelector from "documents/_views/selectors/document-selector";
import type {
  CreateOnboardingTemplateRequest,
  OnboardingTemplateTerms,
  OnboardingTemplateValidationError,
} from "work/onboarding-templates/_schemas/onboarding-template-management-schema";

type EditorValue = {
  templateId: string;
  displayName: string;
  draft: OnboardingTemplateTerms;
};

type EditorErrors = Record<string, string>;

const EditorFields = styled(Stack)(({ theme }) => ({
  paddingTop: theme.spacing(1),
}));

const ThreeColumnFields = styled(Box)(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    gridTemplateColumns: "1fr 1fr 1fr",
  },
}));

const WorkflowVersionControl = styled(FormControlLabel)(() => ({
  alignSelf: "center",
}));

const ToggleFields = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(2),
}));

const FeeCategoryList = styled(Stack)(({ theme }) => ({
  marginTop: theme.spacing(1),
}));

const FeeCategoryRow = styled(Box)(({ theme }) => ({
  alignItems: "start",
  display: "grid",
  gap: theme.spacing(1),
  gridTemplateColumns: "1fr",
  [theme.breakpoints.up("md")]: {
    gridTemplateColumns: "2fr 1fr 1fr auto",
  },
}));

const FeeCategoryActions = styled(Box)(() => ({
  display: "flex",
}));

const AddFeeCategoryButton = styled(Button)(() => ({
  alignSelf: "start",
}));

const TeamMemberRow = styled(Box)(({ theme }) => ({
  alignItems: "start",
  display: "grid",
  gap: theme.spacing(1),
  gridTemplateColumns: "1fr",
  [theme.breakpoints.up("md")]: {
    gridTemplateColumns: "2fr auto auto",
  },
}));

const DocumentPanel = styled(Box)(({ theme }) => ({
  display: "flex",
  marginTop: theme.spacing(1),
  minHeight: 320,
}));

const FieldLabelContent = styled("span")(({ theme }) => ({
  alignItems: "center",
  display: "inline-flex",
  gap: theme.spacing(0.5),
}));

const emptyOnboardingTemplateEditorValue: EditorValue = {
  templateId: "",
  displayName: "",
  draft: {
    version: "1",
    participationMode: "SystemAssisted",
    workflowVersion: 2,
    name: "",
    description: "",
    effectiveDatePolicy: "entityActivationDate",
    endDatePolicy: "none",
    negotiable: false,
    conflictsCheckWaived: true,
    discount: 0,
    feeSchedule: [{ name: "AttorneyHub Services", currency: "USD", fixedAmount: 0 }],
    vendorTeam: [],
    documents: { conflicts: [], vendorPolicies: [] },
  },
};

export function createEmptyOnboardingTemplateEditorValue(): EditorValue {
  return {
    ...structuredClone(emptyOnboardingTemplateEditorValue),
    templateId: crypto.randomUUID(),
  };
}

export default function OnboardingTemplateEditorDialog(props: Readonly<{
  open: boolean;
  mode: "create" | "edit";
  initialValue: EditorValue;
  saving: boolean;
  staleDraft?: boolean;
  serverErrors?: ReadonlyArray<OnboardingTemplateValidationError>;
  onClose: () => void;
  onPreview: (value: EditorValue) => void;
  onReload?: () => void;
  onSubmit: (value: CreateOnboardingTemplateRequest) => Promise<void>;
}>) {
  const {
    open,
    mode,
    initialValue,
    saving,
    onClose,
    onPreview,
    onReload,
    onSubmit,
  } = props;
  const staleDraft = props.staleDraft ?? false;
  const serverErrors = props.serverErrors ?? [];
  const confirm = useConfirmDialog();
  const [value, setValue] = useState<EditorValue>(initialValue);
  const [submitted, setSubmitted] = useState(false);
  const [previousInitialValue, setPreviousInitialValue] = useState(initialValue);

  if (initialValue !== previousInitialValue) {
    setPreviousInitialValue(initialValue);
    setValue(initialValue);
    setSubmitted(false);
  }

  const dirty = JSON.stringify(value) !== JSON.stringify(initialValue);
  const errors = useMemo(() => validateEditorValue(value), [value]);

  const conflictsDocumentIds = useMemo(
    () => value.draft.documents.conflicts.map((id) => Guid.create(id)),
    [value.draft.documents.conflicts]
  );
  const vendorPolicyDocumentIds = useMemo(
    () => value.draft.documents.vendorPolicies.map((id) => Guid.create(id)),
    [value.draft.documents.vendorPolicies]
  );

  const applyDocumentSelection = useCallback((
    bucket: "conflicts" | "vendorPolicies",
    selected: Document[],
    deselected: Document[]
  ) => setValue((current) => {
    const deselectedIds = new Set(deselected.map((document) => document.id.value));
    const kept = current.draft.documents[bucket].filter((id) => !deselectedIds.has(id));
    const added = selected
      .map((document) => document.id.value)
      .filter((id) => !kept.includes(id));
    return {
      ...current,
      draft: {
        ...current.draft,
        documents: { ...current.draft.documents, [bucket]: [...kept, ...added] },
      },
    };
  }), []);

  const handleConflictsSelectionChanged = useCallback(
    (selected: Document[], deselected: Document[]) =>
      applyDocumentSelection("conflicts", selected, deselected),
    [applyDocumentSelection]
  );
  const handleVendorPoliciesSelectionChanged = useCallback(
    (selected: Document[], deselected: Document[]) =>
      applyDocumentSelection("vendorPolicies", selected, deselected),
    [applyDocumentSelection]
  );
  // A template document is chosen, never created from another template here: this editor is
  // where the templates themselves are named.
  const noopCreateFromTemplate = useCallback(() => undefined, []);

  useEffect(() => {
    if (!open || !dirty) return;
    const warnBeforeUnload = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [dirty, open]);

  async function closeEditor() {
    if (saving) return;
    if (dirty) {
      const response = await confirm({
        title: "Discard unsaved template changes?",
        message: "Your unsaved template changes will be lost.",
        okButtonText: "Discard changes",
        cancelButtonText: "Keep editing",
      });
      if (response === ConfirmResponse.Cancel) return;
    }
    onClose();
  }

  async function saveEditor() {
    setSubmitted(true);
    if (Object.keys(errors).length > 0) return;
    await onSubmit(value);
  }

  const fieldError = (path: string) => submitted ? errors[path] : undefined;

  return (
    <Dialog open={open} fullWidth maxWidth="md" onClose={closeEditor}>
      <DialogTitle>{mode === "create" ? "Create onboarding template" : `Edit ${value.templateId} draft`}</DialogTitle>
      <DialogContent>
        <EditorFields spacing={2}>
          {serverErrors.length > 0 && (
            <Alert severity="error">
              <Typography component="div" variant="subtitle2">The server found these problems:</Typography>
              <ul>
                {serverErrors.map((error) => <li key={`${error.path}-${error.message}`}>{error.path}: {error.message}</li>)}
              </ul>
            </Alert>
          )}
          {staleDraft && (
            <Alert
              severity="warning"
              action={onReload ? <Button color="inherit" onClick={onReload}>Reload draft</Button> : undefined}
            >
              This draft changed after you opened it. Your edits remain in the form for review.
            </Alert>
          )}
          {mode === "create" && (
            <Box>
              <TextField
                fullWidth
                label={<FieldLabel label="Display name" tooltip="The name administrators see when selecting and managing this template." />}
                value={value.displayName}
                required
                error={Boolean(fieldError("displayName"))}
                helperText={fieldError("displayName")}
                onChange={(event) => setValue({ ...value, displayName: event.target.value })}
              />
            </Box>
          )}
          <ThreeColumnFields>
            <TextField
              label={<FieldLabel label="Template Revision" tooltip="Identifies this set of terms. Start with 1 and use a new revision for later published changes." />}
              value={value.draft.version}
              required
              disabled={mode === "edit"}
              error={Boolean(fieldError("version"))}
              helperText={fieldError("version")}
              onChange={(event) => setValue({ ...value, draft: { ...value.draft, version: event.target.value } })}
            />
            <TextField
              select
              label={<FieldLabel label="Participation mode" tooltip="Controls whether the onboarding workflow proceeds automatically or waits for staff review." />}
              value={value.draft.participationMode}
              onChange={(event) => setValue({
                ...value,
                draft: { ...value.draft, participationMode: event.target.value as OnboardingTemplateTerms["participationMode"] },
              })}
            >
              <MenuItem value="SystemAssisted">System assisted</MenuItem>
              <MenuItem value="StaffReview">Staff review</MenuItem>
            </TextField>
            <WorkflowVersionControl
              control={<Switch
                checked={value.draft.workflowVersion === 2}
                onChange={(event) => setValue({
                  ...value,
                  draft: { ...value.draft, workflowVersion: event.target.checked ? 2 : 1 },
                })}
              />}
              label={<FieldLabel label="Workflow version 2" tooltip="Turn on to use proposal-based onboarding workflow v2. Turn off to use legacy workflow v1." />}
            />
          </ThreeColumnFields>
          <Alert severity="warning">
            Review participation mode, workflow version, negotiation, fee amounts, and conflicts waiver carefully.
            These settings control the employee and officer workflow.
          </Alert>
          <TextField
            label={<FieldLabel label="Agreement name" tooltip="The title shown to officers when they review and accept these onboarding terms." />}
            value={value.draft.name}
            required
            error={Boolean(fieldError("name"))}
            helperText={fieldError("name")}
            onChange={(event) => setValue({ ...value, draft: { ...value.draft, name: event.target.value } })}
          />
          <TextField
            label={<FieldLabel label="Description" tooltip="Explains the purpose and scope of the agreement to the officer." />}
            value={value.draft.description}
            required
            multiline
            minRows={3}
            error={Boolean(fieldError("description"))}
            helperText={fieldError("description")}
            onChange={(event) => setValue({ ...value, draft: { ...value.draft, description: event.target.value } })}
          />
          <ThreeColumnFields>
            <TextField
              select
              label={<FieldLabel label="Effective date policy" tooltip="Sets when the agreement starts. Activation Date starts it when the organization is activated and is currently the only supported option." />}
              value={value.draft.effectiveDatePolicy}
              required
              error={Boolean(fieldError("effectiveDatePolicy"))}
              helperText={fieldError("effectiveDatePolicy")}
              onChange={(event) => setValue({ ...value, draft: { ...value.draft, effectiveDatePolicy: event.target.value } })}
            >
              <MenuItem value="entityActivationDate">Activation Date</MenuItem>
            </TextField>
            <TextField
              select
              label={<FieldLabel label="End date policy" tooltip="Sets when the agreement ends. None keeps it in effect without a preset end date and is currently the only supported option." />}
              value={value.draft.endDatePolicy}
              required
              error={Boolean(fieldError("endDatePolicy"))}
              helperText={fieldError("endDatePolicy")}
              onChange={(event) => setValue({ ...value, draft: { ...value.draft, endDatePolicy: event.target.value } })}
            >
              <MenuItem value="none">None</MenuItem>
            </TextField>
            <NumericFormat
              label={<FieldLabel label="Discount" tooltip="The default fee discount as a percentage from 0% through 100%." />}
              customInput={TextField}
              allowNegative={false}
              value={percentageValue(value.draft.discount)}
              suffix="%"
              type="text"
              thousandSeparator={false}
              decimalScale={2}
              fixedDecimalScale
              error={Boolean(fieldError("discount"))}
              helperText={fieldError("discount") ?? "Enter a value from 0% through 100%."}
              isAllowed={({ floatValue }) => floatValue == null || (floatValue >= 0 && floatValue <= 100)}
              onValueChange={({ floatValue }) => setValue({
                ...value,
                draft: { ...value.draft, discount: (floatValue ?? 0) / 100 },
              })}
            />
          </ThreeColumnFields>
          <ToggleFields>
            <FormControlLabel
              control={<Switch checked={value.draft.negotiable} onChange={(event) => setValue({
                ...value,
                draft: { ...value.draft, negotiable: event.target.checked },
              })} />}
              label={<FieldLabel label="Negotiable" tooltip="Allows the officer and AttorneyHub to negotiate these terms before acceptance." />}
            />
            <FormControlLabel
              control={<Switch checked={value.draft.conflictsCheckWaived} onChange={(event) => setValue({
                ...value,
                draft: { ...value.draft, conflictsCheckWaived: event.target.checked },
              })} />}
              label={<FieldLabel label="Conflicts check waived" tooltip="Records that a conflicts check is not required for this onboarding agreement." />}
            />
          </ToggleFields>
          <Box>
            <Typography component="h3" variant="h6">Fee categories</Typography>
            {submitted && errors.feeSchedule && <Typography color="error">{errors.feeSchedule}</Typography>}
            <FeeCategoryList spacing={1.5}>
              {value.draft.feeSchedule.map((fee, index) => (
                <FeeCategoryRow key={`fee-${index}`}>
                  <TextField
                    label={<FieldLabel label={`Fee category ${index + 1} name`} tooltip="Names the service category that appears in the onboarding fee schedule." />}
                    value={fee.name}
                    required
                    error={Boolean(fieldError(`feeSchedule.${index}.name`))}
                    helperText={fieldError(`feeSchedule.${index}.name`)}
                    onChange={(event) => {
                      const feeSchedule = [...value.draft.feeSchedule];
                      feeSchedule[index] = { ...fee, name: event.target.value };
                      setValue({ ...value, draft: { ...value.draft, feeSchedule } });
                    }}
                  />
                  <TextField
                    label={<FieldLabel label={`Fee category ${index + 1} currency`} tooltip="The currency used for this fee category. Onboarding templates currently support USD." />}
                    value={fee.currency}
                    disabled
                  />
                  <NumericFormat
                    label={<FieldLabel label={`Fee category ${index + 1} amount`} tooltip="The fixed amount charged for this service category. Enter 0 when no fixed fee applies." />}
                    customInput={TextField}
                    allowNegative={false}
                    value={fee.fixedAmount}
                    prefix="$"
                    type="text"
                    thousandSeparator
                    decimalScale={2}
                    error={Boolean(fieldError(`feeSchedule.${index}.fixedAmount`))}
                    helperText={fieldError(`feeSchedule.${index}.fixedAmount`)}
                    onValueChange={({ floatValue }) => {
                      const feeSchedule = [...value.draft.feeSchedule];
                      feeSchedule[index] = { ...fee, fixedAmount: floatValue ?? 0 };
                      setValue({ ...value, draft: { ...value.draft, feeSchedule } });
                    }}
                  />
                  <FeeCategoryActions>
                    <Tooltip title="Move fee category up"><span><IconButton
                      aria-label={`Move fee category ${index + 1} up`}
                      disabled={index === 0}
                      onClick={() => {
                        const feeSchedule = [...value.draft.feeSchedule];
                        [feeSchedule[index - 1], feeSchedule[index]] = [feeSchedule[index], feeSchedule[index - 1]];
                        setValue({ ...value, draft: { ...value.draft, feeSchedule } });
                      }}
                    ><ArrowUpwardIcon /></IconButton></span></Tooltip>
                    <Tooltip title="Move fee category down"><span><IconButton
                      aria-label={`Move fee category ${index + 1} down`}
                      disabled={index === value.draft.feeSchedule.length - 1}
                      onClick={() => {
                        const feeSchedule = [...value.draft.feeSchedule];
                        [feeSchedule[index], feeSchedule[index + 1]] = [feeSchedule[index + 1], feeSchedule[index]];
                        setValue({ ...value, draft: { ...value.draft, feeSchedule } });
                      }}
                    ><ArrowDownwardIcon /></IconButton></span></Tooltip>
                    <Tooltip title="Remove fee category"><span><DeleteIconButton
                      aria-label={`Remove fee category ${index + 1}`}
                      disabled={value.draft.feeSchedule.length === 1}
                      onClick={() => setValue({
                        ...value,
                        draft: { ...value.draft, feeSchedule: value.draft.feeSchedule.filter((_, itemIndex) => itemIndex !== index) },
                      })}
                    ><DeleteIcon /></DeleteIconButton></span></Tooltip>
                  </FeeCategoryActions>
                </FeeCategoryRow>
              ))}
              <AddFeeCategoryButton
                startIcon={<AddIcon />}
                onClick={() => setValue({
                  ...value,
                  draft: {
                    ...value.draft,
                    feeSchedule: [...value.draft.feeSchedule, { name: "", currency: "USD", fixedAmount: 0 }],
                  },
                })}
              >
                Add fee category
              </AddFeeCategoryButton>
            </FeeCategoryList>
          </Box>
          <Box>
            <Typography component="h3" variant="h6">Attorney Hub team</Typography>
            <Typography color="text.secondary" variant="body2">
              Who appears on our side of the proposal, so the client has someone to contact. Leave
              it empty and the proposal carries the configured onboarding officer alone.
            </Typography>
            {submitted && errors.vendorTeam && <Typography color="error">{errors.vendorTeam}</Typography>}
            <FeeCategoryList spacing={1.5}>
              {value.draft.vendorTeam.map((member, index) => (
                <TeamMemberRow key={`team-${index}`}>
                  <TextField
                    label={<FieldLabel label={`Team member ${index + 1} email`} tooltip="The colleague's Attorney Hub sign-in address. Resolved to their account when a proposal is created, so it stays correct across environments." />}
                    value={member.email ?? ""}
                    error={Boolean(fieldError(`vendorTeam.${index}`))}
                    helperText={fieldError(`vendorTeam.${index}`)}
                    onChange={(event) => {
                      const vendorTeam = [...value.draft.vendorTeam];
                      vendorTeam[index] = { ...member, email: event.target.value, userId: null };
                      setValue({ ...value, draft: { ...value.draft, vendorTeam } });
                    }}
                  />
                  <FormControlLabel
                    control={<Switch
                      checked={member.isLeader}
                      onChange={(event) => {
                        // One leader at most, so choosing one stands the others down rather than
                        // leaving a draft that only fails on save.
                        const vendorTeam = value.draft.vendorTeam.map((candidate, candidateIndex) => ({
                          ...candidate,
                          isLeader: event.target.checked && candidateIndex === index,
                        }));
                        setValue({ ...value, draft: { ...value.draft, vendorTeam } });
                      }}
                    />}
                    label={<FieldLabel label="Team leader" tooltip="Leads our side of the proposal. Without one, the configured onboarding officer leads it." />}
                  />
                  <FeeCategoryActions>
                    <DeleteIconButton
                      aria-label={`Remove team member ${index + 1}`}
                      onClick={() => setValue({
                        ...value,
                        draft: {
                          ...value.draft,
                          vendorTeam: value.draft.vendorTeam.filter((_, itemIndex) => itemIndex !== index),
                        },
                      })}
                    >
                      <DeleteIcon />
                    </DeleteIconButton>
                  </FeeCategoryActions>
                </TeamMemberRow>
              ))}
            </FeeCategoryList>
            <AddFeeCategoryButton
              startIcon={<AddIcon />}
              onClick={() => setValue({
                ...value,
                draft: {
                  ...value.draft,
                  vendorTeam: [...value.draft.vendorTeam, { userId: null, email: "", isLeader: false }],
                },
              })}
            >
              Add team member
            </AddFeeCategoryButton>
          </Box>
          <Box>
            <Typography component="h3" variant="h6">Conflicts statement</Typography>
            <Typography color="text.secondary" variant="body2">
              Attached to every proposal this template creates.
            </Typography>
            <DocumentPanel>
              <DocumentSelector
                documentType="conflicts"
                selectedTab="templates"
                selectedDocumentIds={conflictsDocumentIds}
                onDocumentSelectionChanged={handleConflictsSelectionChanged}
                onCreateDocumentFromTemplate={noopCreateFromTemplate}
              />
            </DocumentPanel>
          </Box>
          <Box>
            <Typography component="h3" variant="h6">Our terms of service</Typography>
            <Typography color="text.secondary" variant="body2">
              Contract documents attached as our policies on every proposal this template creates.
            </Typography>
            <DocumentPanel>
              <DocumentSelector
                documentType="policy"
                selectedTab="templates"
                selectedDocumentIds={vendorPolicyDocumentIds}
                onDocumentSelectionChanged={handleVendorPoliciesSelectionChanged}
                onCreateDocumentFromTemplate={noopCreateFromTemplate}
              />
            </DocumentPanel>
          </Box>
        </EditorFields>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onPreview(value)} disabled={Object.keys(errors).length > 0}>Preview exact terms</Button>
        <Button onClick={closeEditor} disabled={saving}>Cancel</Button>
        <LoadingButton
          variant="contained"
          loading={saving}
          disabled={Object.keys(errors).length > 0}
          onClick={saveEditor}
        >
          {mode === "create" ? "Create" : "Save draft"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

function validateEditorValue(value: EditorValue): EditorErrors {
  const errors: EditorErrors = {};
  if (!value.templateId.trim()) errors.templateId = "Template ID is required.";
  if (!value.displayName.trim()) errors.displayName = "Display name is required.";
  if (!value.draft.version.trim()) errors.version = "Version is required.";
  if (value.draft.workflowVersion !== 1 && value.draft.workflowVersion !== 2) {
    errors.workflowVersion = "Workflow version must be 1 or 2.";
  } else if (value.draft.negotiable && value.draft.workflowVersion < 2) {
    errors.workflowVersion = "A negotiable template requires workflow version 2 or later.";
  }
  if (!value.draft.name.trim()) errors.name = "Agreement name is required.";
  if (!value.draft.description.trim()) errors.description = "Description is required.";
  if (value.draft.effectiveDatePolicy !== "entityActivationDate") {
    errors.effectiveDatePolicy = "Effective date policy must be Activation Date.";
  }
  if (value.draft.endDatePolicy !== "none") errors.endDatePolicy = "End date policy must be None.";
  if (value.draft.discount < 0 || value.draft.discount > 1) errors.discount = "Discount must be from 0 through 1.";
  if (value.draft.feeSchedule.length === 0) errors.feeSchedule = "Add at least one fee category.";
  const feeNames = new Set<string>();
  value.draft.feeSchedule.forEach((fee, index) => {
    const normalizedName = fee.name.trim().toLowerCase();
    if (!normalizedName) errors[`feeSchedule.${index}.name`] = "Fee category name is required.";
    else if (feeNames.has(normalizedName)) errors[`feeSchedule.${index}.name`] = "Fee category names must be unique.";
    feeNames.add(normalizedName);
    if (fee.currency !== "USD") errors[`feeSchedule.${index}.currency`] = "Currency must be USD.";
    if (!Number.isFinite(fee.fixedAmount) || fee.fixedAmount < 0) {
      errors[`feeSchedule.${index}.fixedAmount`] = "Fee amount cannot be negative.";
    }
  });
  const teamIdentities = new Set<string>();
  value.draft.vendorTeam.forEach((member, index) => {
    const email = member.email?.trim() ?? "";
    const hasUserId = Boolean(member.userId) && !Guid.isEmpty(member.userId ?? undefined);
    if (!email && !hasUserId) {
      errors[`vendorTeam.${index}`] = "An email address is required.";
      return;
    }
    const identity = (hasUserId ? member.userId! : email).toLowerCase();
    if (teamIdentities.has(identity)) errors[`vendorTeam.${index}`] = "This person is already on the team.";
    teamIdentities.add(identity);
  });
  if (value.draft.vendorTeam.filter((member) => member.isLeader).length > 1) {
    errors.vendorTeam = "Only one team member can be the leader.";
  }
  return errors;
}

export type { EditorValue as OnboardingTemplateEditorValue };

function FieldLabel({ label, tooltip }: Readonly<{ label: string; tooltip: string }>) {
  return (
    <FieldLabelContent>
      {label}
      <Tooltip arrow title={tooltip}>
        <InfoOutlinedIcon
          aria-label={`${label} information`}
          color="action"
          fontSize="inherit"
        />
      </Tooltip>
    </FieldLabelContent>
  );
}
